import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './ExternalTaskPage.css';

const ExternalTaskPage = () => {
  const { taskId, encryptedBuildingId } = useParams();
  const [task, setTask] = useState(null);
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    status: '',
    taskUser: '',
    updateNote: ''
  });

  const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

  // Load task data on component mount
  useEffect(() => {
    if (taskId && encryptedBuildingId) {
      loadTask();
    } else {
      setError('קישור לא תקין');
      setLoading(false);
    }
  }, [taskId, encryptedBuildingId]);

  useEffect(() => {
  },[task])

  const loadTask = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_URL}/api/ExternalTask/GetTask/${taskId}/${encryptedBuildingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('שגיאה בטעינת המשימה');
      }

      const data = await response.json();
      if (data.success) {

        setTask(data.task);
        setBuilding(data.building);
        setFormData({
          status: data.task.status || '',
          taskUser: data.task.taskUser || '',
          updateNote: ''
        });
      } else {
        setError(data.message || 'שגיאה בטעינת המשימה');
      }
    } catch (err) {
      setError('שגיאה בחיבור לשרת');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.status && !formData.taskUser && !formData.updateNote) {
      setError('נדרש לפחות שדה אחד לעדכון');
      return;
    }

    try {
      setUpdating(true);
      setError('');
      setUpdateSuccess(false);

      // Prepare update data - only send fields that have changed
      const updateData = {};
      
      if (formData.status !== task.Status) {
        updateData.status = formData.status;
      }
      
      if (formData.taskUser !== task.TaskUser) {
        updateData.taskUser = formData.taskUser;
      }
      
      if (formData.updateNote.trim()) {
        updateData.updateNote = formData.updateNote.trim();
      }

      // Check if there are actually changes to make
      if (Object.keys(updateData).length === 0) {
        setError('לא נמצאו שינויים לעדכון');
        return;
      }

      const response = await fetch(`${API_URL}/api/ExternalTask/UpdateTask/${taskId}/${encryptedBuildingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error('שגיאה בעדכון המשימה');
      }

      const data = await response.json();

      if (data.success) {
        setUpdateSuccess(true);
        setFormData(prev => ({ ...prev, updateNote: '' }));
        
        // Update task state with new values
        setTask(prev => ({
          ...prev,
          Status: formData.status,
          TaskUser: formData.taskUser
        }));

        // Show success message and scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Hide success message after 5 seconds
        setTimeout(() => setUpdateSuccess(false), 5000);
      } else {
        setError(data.message || 'שגיאה בעדכון המשימה');
      }
    } catch (err) {
      setError('שגיאה בחיבור לשרת');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('he-IL');
    } catch {
      return dateString;
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'גבוה': return 'priority-high';
      case 'בינוני': return 'priority-medium';
      case 'נמוך': return 'priority-low';
      default: return '';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'פתוח': return 'status-open';
      case 'בטיפול': return 'status-in-progress';
      case 'הושלם': return 'status-completed';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="external-task-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>טוען פרטי משימה...</p>
        </div>
      </div>
    );
  }

  if (error && !task) {
    return (
      <div className="external-task-page">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h2>שגיאה בטעינת המשימה</h2>
          <p>{error}</p>
          <button onClick={loadTask} className="retry-button">
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="external-task-page">
      <div className="task-container">
        {/* Header */}
        <div className="page-header">
          <div className="header-icon">📋</div>
          <div className="header-content">
            <h1>עדכון משימה - ועד הבית</h1>
            <p className="building-info">
              <span className="building-icon">🏢</span>
              {building?.Name || building?.Address}
            </p>
          </div>
        </div>

        {/* Success Message */}
        {updateSuccess && (
          <div className="success-message">
            <div className="success-icon">✅</div>
            <div className="success-content">
              <h3>המשימה עודכנה בהצלחה!</h3>
              <p>השינויים נשמרו במערכת</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <div className="error-content">
              <strong>שגיאה:</strong> {error}
            </div>
          </div>
        )}

        {/* Task Details */}
        <div className="task-details">
          <div className="details-header">
            <h2>פרטי המשימה</h2>
            <div className="task-badges">
              <span className={`status-badge ${getStatusClass(task.status)}`}>
                {task.status}
              </span>
              <span className={`priority-badge ${getPriorityClass(task.priority)}`}>
                {task.priority}
              </span>
            </div>
          </div>

          <div className="details-grid">
            <div className="detail-item">
              <label>מספר משימה:</label>
              <span className="task-id">#{task.tasksID}</span>
            </div>

            <div className="detail-item">
              <label>תאריך יעד:</label>
              <span>{formatDate(task.date)}</span>
            </div>

            <div className="detail-item full-width">
              <label>תיאור המשימה:</label>
              <div className="task-description">
                {task.description?.split('\n').map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>

            {task.TaskImageURL && (
              <div className="detail-item full-width">
                <label>תמונה:</label>
                <div className="task-image">
                  <img 
                    src={task.taskImageURL} 
                    alt="תמונת המשימה"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Update Form */}
        <div className="update-form">
          <div className="form-header">
            <h2>עדכון המשימה</h2>
            <p>ניתן לעדכן את סטטוס המשימה, האחראי ולהוסיף הערות</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="status">סטטוס משימה:</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={updating}
                >
                  <option value="פתוח">פתוח</option>
                  <option value="בטיפול">בטיפול</option>
                  <option value="הושלם">הושלם</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="taskUser">אחראי לביצוע:</label>
                <input
                  type="text"
                  id="taskUser"
                  name="taskUser"
                  value={formData.taskUser}
                  onChange={handleInputChange}
                  placeholder="שם האחראי לביצוע המשימה"
                  disabled={updating}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="updateNote">הערות עדכון:</label>
                <textarea
                  id="updateNote"
                  name="updateNote"
                  value={formData.updateNote}
                  onChange={handleInputChange}
                  placeholder="הוסף הערות על העדכון (אופציונלי)"
                  rows="4"
                  disabled={updating}
                />
                <small className="form-help">
                  ההערות יתווספו לתיאור המשימה עם חותמת זמן
                </small>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={updating}
              >
                {updating ? (
                  <>
                    <div className="button-spinner"></div>
                    מעדכן...
                  </>
                ) : (
                  <>
                    <span className="button-icon">💾</span>
                    עדכן משימה
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={loadTask}
                className="refresh-button"
                disabled={updating}
              >
                <span className="button-icon">🔄</span>
                רענן נתונים
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="page-footer">
          <p>
            <span className="footer-icon">🏠</span>
            מערכת ניהול ועד בית - עדכון חיצוני
          </p>
          <p className="footer-note">
            דף זה מאפשר עדכון מוגבל של המשימה ללא צורך בהרשמה למערכת
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExternalTaskPage;